'use client'

import React, { useEffect, useRef } from 'react'
import { Stack } from '@mantine/core'
import type { Sku } from '@/types/interfaces/sku'
import { trackEvent, SegmentEvents } from '@nile/mantine-theme/analytics'

import type { SkuRefreshPageConfig } from '@/types/enums/sku-component-types'
import SkuRefreshRenderer, { type VisitOptionsData } from '@/components/Refresh/SkuPages/SkuRefreshRenderer'
import { SkuStickyCta } from '@/components/Refresh/SkuPages/SkuStickyCta'

export default function SkuBodyRenderer({
    sku,
    refreshConfig,
    visitOptionsData,
    inPersonAvailable,
    skuRefreshEnabled,
    refreshFlagKey,
    isLabSku,
    skuStickyCtaEnabled,
    children,
}: {
    sku: Sku
    refreshConfig: SkuRefreshPageConfig | null
    visitOptionsData: VisitOptionsData | null
    inPersonAvailable: boolean
    skuRefreshEnabled: boolean
    refreshFlagKey: string
    isLabSku: boolean
    skuStickyCtaEnabled: boolean
    children: React.ReactNode
}) {
    const hasRefreshConfig = refreshConfig != null && refreshConfig.components.length > 0
    const isRefresh = skuRefreshEnabled && hasRefreshConfig
    const inStickyCtaExperiment = isRefresh && !isLabSku
    const includesAcr =
        [
            visitOptionsData?.response?.sku_price_mapping?.find(
                (mapping) => String(mapping.id) === String(sku.id),
            )?.prices,
        ]
            .flat()
            .flatMap((price) => price?.options ?? [])
            .some((option) => option.allows_acr === true)

    useEffect(() => {
        window.DD_RUM?.onReady?.(() => {
            window.DD_RUM?.setViewContextProperty?.('includes_acr', includesAcr)
        })
    }, [includesAcr])

    const hasTracked = useRef(false)
    useEffect(() => {
        if (!hasRefreshConfig || hasTracked.current) return
        hasTracked.current = true

        const anonymousId = window.analytics?.user?.()?.anonymousId?.() ?? null
        const datadogSessionId = window.DD_RUM?.getInternalContext?.()?.session_id ?? null

        window.DD_RUM?.onReady?.(() => {
            window.DD_RUM?.addFeatureFlagEvaluation?.(refreshFlagKey, skuRefreshEnabled)
        })

        trackEvent(SegmentEvents.SKU_PAGE_VARIANT, {
            sku_id: sku.id,
            variant: skuRefreshEnabled ? 'refresh' : 'legacy',
            flag_key: refreshFlagKey,
            anonymous_id: anonymousId,
            datadog_session_id: datadogSessionId,
        })
    }, [hasRefreshConfig, skuRefreshEnabled, refreshFlagKey, sku.id])

    const hasTrackedStickyCta = useRef(false)
    useEffect(() => {
        if (!inStickyCtaExperiment || hasTrackedStickyCta.current) return
        hasTrackedStickyCta.current = true

        const anonymousId = window.analytics?.user?.()?.anonymousId?.() ?? null
        const datadogSessionId = window.DD_RUM?.getInternalContext?.()?.session_id ?? null

        window.DD_RUM?.onReady?.(() => {
            window.DD_RUM?.addFeatureFlagEvaluation?.('sku-sticky-cta-refresh', skuStickyCtaEnabled)
        })

        trackEvent(SegmentEvents.SKU_STICKY_CTA_VARIANT, {
            sku_id: sku.id,
            variant: skuStickyCtaEnabled ? 'sticky_cta' : 'inline_cta',
            anonymous_id: anonymousId,
            datadog_session_id: datadogSessionId,
        })
    }, [inStickyCtaExperiment, skuStickyCtaEnabled, sku.id])

    return (
        <Stack
            bg="var(--gm-background-bg-surface)"
            px={isRefresh ? { base: 0, md: 0 } : { base: 24, md: 72 }}
            pt={12}
            pb={{ base: skuStickyCtaEnabled ? 112 : 12, md: 12 }}
            style={{ overflowX: 'hidden' }}
        >
            {isRefresh ? (
                <SkuRefreshRenderer
                    sku={sku}
                    config={refreshConfig!}
                    visitOptionsData={visitOptionsData}
                    inPersonAvailable={inPersonAvailable}
                    skuStickyCtaEnabled={skuStickyCtaEnabled}
                />
            ) : (
                children
            )}
            {skuStickyCtaEnabled && <SkuStickyCta sku={sku} variant={isRefresh ? 'refresh' : 'legacy'} />}
        </Stack>
    )
}
